import React, { useState } from 'react';
import { TextField, Button, Typography, Container, IconButton, InputAdornment, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/system';
import AxiosInstance from './Axios';
import { useParams, Link } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(4),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const ResetPassword = () => {
  const { uid, token } = useParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await AxiosInstance.post(`/Academy/api/reset-password/${uid}/${token}/`, {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ minHeight: 600 }}>
      <Typography variant="h4" align="center" gutterBottom sx={{ marginBottom: 2, marginTop: 10 }}>
        Reset Password
      </Typography>

      {success ? (
        <>
          <Alert severity="success" sx={{ mt: 2 }}>
            Your password has been reset successfully.
          </Alert>
          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            <Link to="/Login/" style={{ color: '#1976d2' }}>Click here to log in</Link>
          </Typography>
        </>
      ) : (
        <StyledForm onSubmit={handleSubmit}>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Enter and confirm your new password below.
          </Typography>

          <StyledTextField
            label="New Password"
            type={showNew ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            label="Confirm New Password"
            type={showConfirm ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          <StyledButton
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{ paddingTop: 2, paddingBottom: 2, marginBottom: 4 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
          </StyledButton>
        </StyledForm>
      )}
    </Container>
  );
};

export default ResetPassword;
